import { SettingsManager as BaseSettingsManager } from '@bn-games/settings-core';
import { normalizeVariantId } from '../game/variants/index.js';
import { DEFAULT_SETTINGS, type Settings } from './types.js';

const STORAGE_KEY = 'domino-settings';

function merge(stored: Record<string, unknown>, defaults: Settings): Settings {
  return {
    sound: typeof stored['sound'] === 'boolean' ? stored['sound'] : defaults.sound,
    theme:
      stored['theme'] === 'mahogany' ||
      stored['theme'] === 'persian' ||
      stored['theme'] === 'deco' ||
      stored['theme'] === 'monte'
        ? stored['theme']
        : defaults.theme,
    language:
      stored['language'] === 'en' || stored['language'] === 'ru' ? stored['language'] : defaults.language,
    playerName:
      typeof stored['playerName'] === 'string' && (stored['playerName'] as string).trim().length > 0
        ? (stored['playerName'] as string).trim()
        : defaults.playerName,
    // Legacy ids (`kozyol` → `telefon`) map on read; the stored blob is not rewritten.
    variant: normalizeVariantId(stored['variant']) ?? defaults.variant,
    playerCount:
      stored['playerCount'] === 2 || stored['playerCount'] === 3 || stored['playerCount'] === 4
        ? stored['playerCount']
        : defaults.playerCount,
    difficulty:
      stored['difficulty'] === 'easy' || stored['difficulty'] === 'medium' || stored['difficulty'] === 'hard'
        ? stored['difficulty']
        : defaults.difficulty,
    pointGoal:
      stored['pointGoal'] === 100 || stored['pointGoal'] === 150 || stored['pointGoal'] === 250
        ? stored['pointGoal']
        : defaults.pointGoal,
    mugginsStrict:
      typeof stored['mugginsStrict'] === 'boolean' ? stored['mugginsStrict'] : defaults.mugginsStrict,
    // Persisted under a new key on purpose: the old `mugginsAutoClaim` field
    // defaulted to false and was saved into every existing settings blob, so
    // reading it would keep auto-claim off for everyone after the default flip.
    autoClaimMuggins:
      typeof stored['autoClaimMuggins'] === 'boolean'
        ? stored['autoClaimMuggins']
        : defaults.autoClaimMuggins,
    hotSeatHide:
      typeof stored['hotSeatHide'] === 'boolean' ? stored['hotSeatHide'] : defaults.hotSeatHide,
    afkAssistEnabled:
      typeof stored['afkAssistEnabled'] === 'boolean'
        ? stored['afkAssistEnabled']
        : defaults.afkAssistEnabled,
    afkTimeoutMs:
      typeof stored['afkTimeoutMs'] === 'number' && Number.isFinite(stored['afkTimeoutMs'])
        ? (stored['afkTimeoutMs'] as number)
        : defaults.afkTimeoutMs,
    afkRemindersEnabled:
      typeof stored['afkRemindersEnabled'] === 'boolean'
        ? stored['afkRemindersEnabled']
        : defaults.afkRemindersEnabled,
    showAfkPingButton:
      typeof stored['showAfkPingButton'] === 'boolean'
        ? stored['showAfkPingButton']
        : defaults.showAfkPingButton,
    turnStartSound:
      typeof stored['turnStartSound'] === 'boolean'
        ? stored['turnStartSound']
        : defaults.turnStartSound,
    verifiableDiceEnabled:
      typeof stored['verifiableDiceEnabled'] === 'boolean'
        ? stored['verifiableDiceEnabled']
        : defaults.verifiableDiceEnabled,
    showBoneyardCount:
      typeof stored['showBoneyardCount'] === 'boolean'
        ? stored['showBoneyardCount']
        : defaults.showBoneyardCount,
    goatOpening13:
      typeof stored['goatOpening13'] === 'boolean' ? stored['goatOpening13'] : defaults.goatOpening13,
    goatBaldFinish:
      typeof stored['goatBaldFinish'] === 'boolean' ? stored['goatBaldFinish'] : defaults.goatBaldFinish,
  };
}

export class SettingsManager extends BaseSettingsManager<Settings> {
  constructor() {
    super(STORAGE_KEY, DEFAULT_SETTINGS, merge);
  }
}
